import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Container,
  Box,
  List,
  Link as MuiLink,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// MUI Icons
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedIcon from "@mui/icons-material/Verified";
import PublicIcon from "@mui/icons-material/Public";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MemoryIcon from "@mui/icons-material/Memory";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import HandshakeIcon from "@mui/icons-material/Handshake";
import DescriptionIcon from "@mui/icons-material/Description";
import CodeIcon from "@mui/icons-material/Code";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LanguageIcon from "@mui/icons-material/Language";
import BugReportIcon from "@mui/icons-material/BugReport";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import PolicyIcon from "@mui/icons-material/Policy";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";

// Import common components
import EmbededContactForm from "./EmbededContactForm";
import SetDocumentMeta from "../common/SetDocumentMeta";
import InfoBox from "./utils/InfoBox";
import { getImageUrl } from "../../utils/helpers";
import KthSoftLicTable from "./KthSoftLicTable";

// Feature card component for displaying key features or services
const FeatureCard = ({ title, icon, content, color = "#4ABDAC" }) => {
  return (
    <Card
      elevation={1}
      sx={{
        height: "100%",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box sx={{ color, mr: 1.5 }}>{icon}</Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color, fontWeight: "medium" }}
          >
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2">{content}</Typography>
      </CardContent>
    </Card>
  );
};

// Define "Jump to Section" items with icons
const jumpToSections = [
  {
    id: "copyright-overview",
    label: "Copyright Fundamentals",
    icon: <DescriptionIcon sx={{ mr: 1 }} />,
  },
  {
    id: "software-copyright",
    label: "Software Copyright Protection",
    icon: <CodeIcon sx={{ mr: 1 }} />,
  },
  {
    id: "patent-vs-copyright",
    label: "Patents vs Copyright vs Trade Secrets",
    icon: <AccountBalanceIcon sx={{ mr: 1 }} />,
  },
  {
    id: "software-licensing",
    label: "Software Licensing Models",
    icon: <HandshakeIcon sx={{ mr: 1 }} />,
  },
  {
    id: "eula-section",
    label: "End User License Agreements",
    icon: <PolicyIcon sx={{ mr: 1 }} />,
  },
  {
    id: "ai-agreements",
    label: "Other Legal Agreements for AI",
    icon: <LanguageIcon sx={{ mr: 1 }} />,
    externalLink: "/legal-agreements-for-ai",
  },
  {
    id: "foss-risks",
    label: "Free & Open Source (FOSS) Licensing Risks",
    icon: <BugReportIcon sx={{ mr: 1 }} />,
  },
  {
    id: "foss-licenses-table",
    label: "Example FOSS Licenses",
    icon: <LibraryBooksIcon sx={{ mr: 1 }} />,
  },

  {
    id: "open-source-ai",
    label: "Open Source AI Models",
    icon: <SchoolIcon sx={{ mr: 1 }} />,
    externalLink: "/ai-models?sourceType=open_source",
  },
  {
    id: "contact-section",
    label: "Schedule a Consultation",
    icon: <PermContactCalendarIcon sx={{ mr: 1 }} />,
  },
];

const apiUrlKth = `${window._env_.REACT_APP_PATANALAI_API_URL}`;

const KthSoftLic = ({ baseConfig }) => {
  // SEO metadata
  SetDocumentMeta({
    title: "Software Licensing & Copyright Protection | Kama Thuo, PLLC",
    description:
      "Expert legal guidance on software licensing, copyright protection, and open source compliance for technology and AI companies. Navigate the complex landscape of FOSS licenses and software patents.",
    keywords:
      "software licensing, copyright protection, open source licensing, FOSS compliance, software patents, AI licensing, EULA, software agreements, technology licensing attorney, copyright law, open source license review, commercial software licensing, software trade secrets, GPL compliance, BSD license, MIT license, Apache license, software development agreements, technology law",
  });

  const {
    priColor,
    priHoverColor,
    secColor,
    tertColor,
    tertHoverColor,
    quadColor,
    sextColor,
    linkColor,
    headerTextColor,
  } = baseConfig.siteBranding;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [licenses, setLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareBy, setCompareBy] = useState("permissive_use");

  useEffect(() => {
    const fetchLicenses = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${apiUrlKth}/kth-ip/foss`);
        setLicenses(response.data);
      } catch (err) {
        setError("Failed to fetch license data.");
        console.error("Error fetching licenses:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLicenses();
  }, []);

  const ComparisonOptions = [
    { value: "permissive_use", label: "Permissive Use" },
    { value: "attribution", label: "Attribution Required" },
    { value: "derivative_works", label: "Derivative Works" },
    { value: "patent_grant", label: "Patent Grant" },
    { value: "commercial_use", label: "Commercial Use" },
    { value: "copyleft", label: "Copyleft" },
  ];

  const handleCompareChange = (e) => {
    setCompareBy(e.target.value);
  };

  // Custom styles
  const sectionStyles = {
    header: {
      py: 3,
      px: isMobile ? 2 : 4,
      mb: 4,
      borderRadius: 2,
      backgroundImage: `linear-gradient(135deg, ${tertColor}22 0%, ${tertColor}44 100%)`,
      boxShadow: "0 3px 5px rgba(0, 0, 0, 0.1)",
    },
    sectionTitle: {
      px: 2,
      py: 1.5,
      mb: 2,
      borderRadius: 1,
      backgroundColor: tertColor,
      color: "white",
      fontWeight: "medium",
      display: "flex",
      alignItems: "center",
    },
    card: {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      borderRadius: 2,
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        transform: "translateY(-2px)",
      },
    },
    accordionTitle: {
      fontWeight: "medium",
      color: "text.primary",
    },
  };

  // Function to scroll to a specific section
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "3rem",
          marginBottom: "3rem",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Container>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ my: 3 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        </Grid>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }} className="link-no-decoration">
      {/* ======== PAGE HEADER ======== */}
      <Box component="header" sx={sectionStyles.header}>
        {/* Banner / Headline */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            color="text.primary"
          >
            Software Licensing & Copyright Protection
          </Typography>
          <Typography
            variant="h6"
            fontWeight="medium"
            color="text.secondary"
            gutterBottom
          >
            Legal Expertise for Technology Companies & AI Developers
          </Typography>
          <Divider
            sx={{ my: 2, width: "20%", mx: "auto", borderColor: tertColor }}
          />
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            lineHeight: 1.8,
            textAlign: "center",
            maxWidth: "85%",
            mx: "auto",
            fontSize: "1.1rem",
          }}
        >
          <strong>In today's complex technology landscape,</strong> proper
          software licensing and copyright protection are crucial to maintaining
          your competitive edge. We provide specialized legal guidance to help
          technology companies and AI developers navigate intellectual property
          protection, licensing compliance, and risk management for their
          software assets.
        </Typography>
      </Box>

      {/* Warning/Caution Box at the top */}
      <InfoBox
        title="CAUTION: Using 'Free' Software Without Understanding the Licenses"
        content="Incorporating third-party libraries, packages, or frameworks without thoroughly reviewing their licenses can expose your business to significant legal and financial risks. Many developers mistakenly assume 'free' means 'free to use without restrictions,' but open source licenses often come with specific obligations that may conflict with your business model or intellectual property strategy."
        type="warning"
        icon={<WarningIcon />}
      />

      {/* Banner for scheduling */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: linkColor,
          p: 2,
          borderRadius: 2,
          mt: 2,
          mb: 2,
          cursor: "pointer",
        }}
        onClick={() => {
          const ctaSection = document.getElementById("contact-section");
          if (ctaSection) {
            ctaSection.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        <PhoneInTalkIcon sx={{ mr: 1, color: "white" }} />
        <Typography variant="body1" sx={{ color: "white" }}>
          <strong>Schedule a Software License Review Consultation</strong> to
          ensure your technology assets are properly protected and compliant!
        </Typography>
      </Box>

      {/* ======== TABLE OF CONTENTS ======== */}
      <Card variant="outlined" sx={{ ...sectionStyles.card, mb: 4 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="medium"
            sx={{
              color: tertColor,
              borderBottom: `2px solid ${tertColor}`,
              pb: 1,
            }}
          >
            Quick Navigation
          </Typography>

          <List dense disablePadding>
            {jumpToSections.map((section) => (
              <ListItem key={section.id} disablePadding>
                {section.externalLink ? (
                  <ListItemButton
                    component={RouterLink}
                    to={section.externalLink}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: `${tertColor}22`,
                        pl: 2,
                      },
                    }}
                  >
                    <Box sx={{ color: tertColor }}>{section.icon}</Box>
                    <ListItemText primary={section.label} />
                    <LaunchIcon
                      sx={{ fontSize: 16, ml: 1, color: linkColor }}
                    />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: `${tertColor}22`,
                        pl: 2,
                      },
                    }}
                  >
                    <Box sx={{ color: tertColor }}>{section.icon}</Box>
                    <ListItemText primary={section.label} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* ======== MAIN CONTENT SECTIONS ======== */}

      {/* 1. Copyright Protection Overview */}
      <Box id="copyright-overview" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <DescriptionIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Copyright Protection Fundamentals
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Copyright protection is a foundational element of intellectual
              property law that automatically applies to original works fixed in
              a tangible medium of expression. For software and technology
              companies, copyright serves as a primary mechanism to protect
              source code, user interfaces, documentation, and other creative
              elements of software applications.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    What Copyright Protects
                  </Typography>
                  <List dense>
                    {[
                      "Original source code and object code",
                      "Graphical user interfaces and visual elements",
                      "Technical documentation and manuals",
                      "Database structures and compilations",
                      "Original AI training sets and datasets (with limitations)",
                      "Creative outputs of AI systems (subject to evolving legal interpretations)",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    What Copyright Does NOT Protect
                  </Typography>
                  <List dense>
                    {[
                      "Functionality, ideas, or concepts underlying the software",
                      "Algorithms, formulas, or mathematical processes in isolation",
                      "Standard programming techniques or industry conventions",
                      "APIs and interfaces necessary for interoperability - per Oracle v. Google (2021)",
                      "Facts, data, or information in isolation from creative arrangement",
                      "Software methods or processes (better protected by patents)",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <InfoBox
              title="Copyright Duration and Protection"
              content="Copyright protection begins automatically at the moment of creation and lasts for the life of the author plus 70 years. For works made for hire (such as software created by employees), copyright lasts for 95 years from publication or 120 years from creation, whichever is shorter. While registration is not required for protection, it offers significant advantages including the ability to sue for infringement and to recover statutory damages and attorney's fees."
              type="primary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 2. Software Copyright Protection */}
      <Box id="software-copyright" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <CodeIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Software Copyright Protection
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Software presents unique challenges for copyright protection due
              to its functional nature and the various elements that comprise a
              complete software product. Understanding the scope and limitations
              of copyright for software is essential for technology companies.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <FeatureCard
                  title="Registration Strategies"
                  icon={<VerifiedIcon />}
                  content="While copyright exists automatically, registration with the U.S. Copyright Office provides critical advantages including legal presumption of validity, ability to sue for infringement, and availability of statutory damages and attorney's fees. We help determine optimal registration strategies for your software assets."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FeatureCard
                  title="Deposit Requirements"
                  icon={<DescriptionIcon />}
                  content="Software copyright registration requires submitting a portion of your source code. We help navigate special deposit requirements for software that contains trade secrets, allowing you to protect confidential code while still securing copyright registration."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FeatureCard
                  title="Ownership Issues"
                  icon={<BusinessIcon />}
                  content="Software development often involves multiple contributors, contractors, and licensed components. We help clarify ownership through proper work-for-hire agreements, contributor licenses, and chain-of-title documentation to ensure your company maintains clear rights to its software assets."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FeatureCard
                  title="Infringement Analysis"
                  icon={<SearchIcon />}
                  content="Software copyright infringement can be difficult to detect and prove. We conduct thorough code comparisons and analysis to identify potential infringement, focusing on substantial similarity of protected expression rather than merely similar functionality or concepts."
                  color={tertColor}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Special Considerations for AI Software
              </Typography>
              <Typography variant="body2" paragraph>
                Artificial intelligence presents novel copyright questions that
                are still evolving in the legal landscape:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Training Data Issues
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The use of copyrighted materials to train AI systems
                        raises complex legal questions around fair use and
                        licensing. Recent litigation is shaping this evolving
                        area of law, and proper documentation of data sources
                        and usage rights is essential.
                      </Typography>
                      <Button
                        variant="text"
                        component={RouterLink}
                        to="/legal-agreements-for-ai#ai-data-agreements"
                        size="small"
                        sx={{ mt: 1, color: tertColor }}
                        endIcon={<LaunchIcon />}
                      >
                        AI Data & Training Agreements
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI-Generated Outputs
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The copyright status of AI-generated content remains
                        uncertain. While the U.S. Copyright Office has rejected
                        registration for works created solely by AI,
                        human-directed AI outputs may be protectable to the
                        extent they reflect human creativity and direction.
                      </Typography>
                      <Button
                        variant="text"
                        component={RouterLink}
                        to="/legal-agreements-for-ai"
                        size="small"
                        sx={{ mt: 1, color: tertColor }}
                        endIcon={<LaunchIcon />}
                      >
                        AI Agreements
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Copyright Notice Best Practices"
              content="While not legally required, we recommend including a proper copyright notice (© [Year] [Company Name]) in your software, documentation, and user interfaces. This prevents an 'innocent infringement' defense, puts others on notice of your rights, and identifies who to contact for licensing. For regularly updated software, use a range of years (e.g., © 2020-2025) covering the first publication to the most recent version."
              type="primary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 3. Patents vs. Copyright vs. Trade Secrets */}
      <Box id="patent-vs-copyright" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <AccountBalanceIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Software Protection Strategy: Patents, Copyright, or Trade
              Secrets?
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              A comprehensive protection strategy for software often involves a
              strategic combination of different intellectual property tools.
              Each mechanism offers distinct advantages and limitations, and the
              optimal approach depends on your specific technology, business
              model, and competitive landscape.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#f5f7fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: tertColor }}
                  >
                    Software Patents
                  </Typography>
                  <List>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="What They Protect"
                        secondary="Novel, non-obvious technical innovations like unique algorithms, methods of operation, and technological processes implemented in software."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Advantages"
                        secondary="Protect functional aspects and methods; provide exclusive rights to implementation; can cover broad concepts; last for ~20 years; create strong barriers to entry."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Challenges"
                        secondary="Subject to patentability challenges under 35 USC §101 (Alice/Mayo framework); high cost and long prosecution time; public disclosure required; may be difficult to detect infringement."
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#f5f7fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: tertColor }}
                  >
                    Copyright Protection
                  </Typography>
                  <List>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="What It Protects"
                        secondary="The expression of code, not the underlying functionality; protects against direct copying and derivative works based on the original code."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Advantages"
                        secondary="Automatic protection upon creation; low-cost registration; long duration (95+ years); international protection via Berne Convention; well-established legal framework."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Challenges"
                        secondary="Does not protect functionality or ideas; limited protection against independent creation; clean room engineering can bypass; reverse engineering may be permitted for interoperability."
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#f5f7fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: tertColor }}
                  >
                    Trade Secrets
                  </Typography>
                  <List>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="What They Protect"
                        secondary="Confidential business information that provides competitive advantage; can include algorithms, formulas, techniques, and processes that are kept secret."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Advantages"
                        secondary="No registration required; potentially perpetual protection; no disclosure necessary; protection against misappropriation; suitable for rapidly evolving technology."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Challenges"
                        secondary="Lost upon public disclosure; requires continuous security measures; no protection against independent discovery or reverse engineering; difficult to enforce; requires strict confidentiality protocols."
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Software Patent Eligibility Challenges
              </Typography>
              <Typography variant="body2" paragraph>
                The Supreme Court's Alice Corp. v. CLS Bank decision established
                a two-part test for software patent eligibility under 35 U.S.C.
                §101:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Step 1: Abstract Idea Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Determine if the claims are directed to an abstract idea
                        (e.g., mathematical formulas, fundamental economic
                        practices, or mental processes). Most software
                        inventions face challenges at this step.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Step 2: Inventive Concept
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        If directed to an abstract idea, examine whether the
                        claims add "significantly more" – an inventive concept
                        that transforms the abstract idea into a patent-eligible
                        application. The technical improvement must be specific
                        and substantial.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                We are experienced at navigating these challenges, drafting
                patent applications that emphasize technical improvements,
                specific implementations, and technological solutions that go
                beyond abstract concepts.
              </Typography>
            </Box>

            <InfoBox
              title="Strategic Multi-Layer Protection"
              content="For many software companies, the optimal approach is a layered strategy: (1) Use patents for core innovative technical solutions, (2) Rely on copyright for implementation code and user-facing elements, (3) Maintain trade secret protection for algorithms and processes that provide competitive advantage and would be difficult to reverse engineer, and (4) Use contracts and licenses to establish usage rights and restrictions. We help tailor this strategy to your specific technology assets and business objectives."
              type="primary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 4. Software Licensing Models */}
      <Box id="software-licensing" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <HandshakeIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Software Licensing Models
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Software licensing is the legal framework that governs how
              software can be distributed, installed, used, and modified. The
              right licensing model is crucial to monetizing your software while
              protecting your intellectual property rights and controlling how
              your software is used in the marketplace.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Proprietary Licensing"
                  icon={<VpnLockIcon />}
                  content="Traditional model where licensees receive the right to use software under specific conditions without access to source code. Includes perpetual licenses, subscription models, usage-based licensing, and other commercial arrangements that maintain maximum control."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Hybrid Licensing"
                  icon={<ShieldIcon />}
                  content="Combined models such as freemium (basic version free, premium version paid), dual licensing (offering both proprietary and open source versions), and open core (open source core with proprietary extensions). These models balance accessibility with revenue generation."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Open Source Licensing"
                  icon={<PublicIcon />}
                  content="Licenses that grant users the right to use, modify, and distribute the source code, subject to various conditions. Options range from permissive licenses with minimal restrictions to copyleft licenses requiring derivative works to be shared under similar terms."
                  color={tertColor}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Key Licensing Considerations
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI Model Licensing
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Specialized considerations for AI technologies,
                        including rights to training data, model outputs,
                        improvements, and fine-tuning. We draft agreements that
                        address the unique aspects of AI licensing, such as
                        inference rights, deployment restrictions, and ethical
                        use limitations.
                      </Typography>
                      <Button
                        variant="text"
                        component={RouterLink}
                        to="/legal-agreements-for-ai#ai-model-licensing"
                        size="small"
                        sx={{ mt: 1, color: tertColor }}
                        endIcon={<LaunchIcon />}
                      >
                        AI Model Licensing Agreements
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        SaaS Agreements
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cloud-based software delivery models require specialized
                        agreements covering service availability, data security,
                        privacy, maintenance, and updates. We draft
                        comprehensive SaaS agreements that protect your
                        interests while meeting customer expectations for
                        service quality and data handling.
                      </Typography>
                      <Button
                        variant="text"
                        component={RouterLink}
                        to="/legal-agreements-for-ai#ai-saas-agreements"
                        size="small"
                        sx={{ mt: 1, color: tertColor }}
                        endIcon={<LaunchIcon />}
                      >
                        AI SaaS Agreements
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        API Licensing
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        APIs require specialized licensing terms addressing rate
                        limits, authentication, permitted use cases, and
                        integration rights. We develop API license agreements
                        that protect your backend systems while enabling the
                        ecosystem growth and integration flexibility that APIs
                        are designed to support.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Enterprise Licensing
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Large-scale deployments requiring customized terms for
                        volume, support levels, customization rights, and
                        implementation services. We negotiate enterprise
                        agreements that scale with your customers' needs while
                        maintaining appropriate protections for your
                        intellectual property.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Training Data Licensing Considerations"
              content="The emerging field of training data licensing for AI systems presents novel legal questions. Rights to use data for training, requirements for data provenance documentation, downstream limitations on model capabilities, and attribution requirements are critical considerations. We help navigate this evolving landscape with agreements that account for current litigation trends and emerging best practices."
              type="primary"
              link="/legal-agreements-for-ai#ai-data-agreements"
              linkText="AI Data Agreements"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 5. End User License Agreements */}
      <Box id="eula-section" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <PolicyIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              End User License Agreements (EULAs)
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              End User License Agreements (EULAs) are critical legal documents
              for software that is distributed to users. A well-drafted EULA
              establishes the terms under which users may use your software,
              protects your intellectual property rights, limits your liability,
              and sets clear expectations about permitted and prohibited
              activities.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Essential EULA Provisions
                  </Typography>
                  <List dense>
                    {[
                      "Scope of license grant (personal, commercial, enterprise use)",
                      "Usage restrictions and prohibited activities",
                      "Intellectual property rights protection clauses",
                      "Warranty disclaimers and liability limitations",
                      "Termination conditions and consequences",
                      "Dispute resolution mechanisms and governing law",
                      "Data collection, privacy practices, and user consent",
                      "Maintenance, support, and update policies",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant="text"
                    component={RouterLink}
                    to="/legal-agreements-for-ai#ai-saas-agreements"
                    size="small"
                    sx={{ mt: 1, color: tertColor }}
                    endIcon={<LaunchIcon />}
                  >
                    End User License Agreements
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Special Considerations for AI Applications
                  </Typography>
                  <List dense>
                    {[
                      "Ownership and rights to AI-generated outputs",
                      "Training data usage rights and limitations",
                      "Model fine-tuning permissions and restrictions",
                      "Ethical use limitations and prohibited applications",
                      "Transparency about AI capabilities and limitations",
                      "Allocation of liability for AI-driven decisions",
                      "Compliance with emerging AI regulations",
                      "Automatic content filtering and moderation standards",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant="text"
                    component={RouterLink}
                    to="/legal-agreements-for-ai"
                    size="small"
                    sx={{ mt: 1, color: tertColor }}
                    endIcon={<LaunchIcon />}
                  >
                    AI-Specific Legal Agreements
                  </Button>
                </Paper>
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                EULA Enforcement Considerations
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Clickwrap vs. Browsewrap
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Courts generally favor clickwrap agreements (requiring
                        active acceptance) over browsewrap agreements (passive
                        acceptance through use). We help implement legally
                        robust acceptance mechanisms that maximize
                        enforceability of your EULA terms.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        International Considerations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        EULAs must account for jurisdictional differences in
                        consumer protection laws, contract formation
                        requirements, and limitations on liability. We design
                        agreements that are adaptable to global distribution
                        while maintaining enforceability in key markets.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Custom EULA Development"
              content="Generic EULAs rarely provide adequate protection for specialized software products. We develop customized agreements tailored to your specific software functionality, business model, and risk profile. Our EULAs are designed to be user-friendly while providing robust legal protection for your intellectual property and limiting potential liability exposure."
              type="primary"
              link="#contact-section"
              linkText="Discuss EULA Development"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 6. FOSS Risks */}
      <Box id="foss-risks" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <BugReportIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              FOSS Licensing Risks in Commercial Applications
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Free and Open Source Software (FOSS) offers tremendous benefits
              for accelerating development, but incorporating FOSS components
              into commercial applications can create significant legal risks if
              not managed properly. Understanding these risks is essential for
              technology companies leveraging open source code.
            </Typography>

            <InfoBox
              title="Risk Assessment Services"
              content="Our firm provides comprehensive open source license audits and risk assessments for your software products. We can review your application's dependency tree, identify potential license compliance issues, and develop strategies to mitigate risks while maintaining the benefits of open source components."
              type="primary"
              icon={<SearchIcon />}
            />

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#fff3e0",
                    border: "1px solid #ffb74d",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#e65100" }}
                  >
                    Key FOSS Licensing Risks
                  </Typography>
                  <List>
                    {[
                      "Copyleft contamination - Strong copyleft licenses may require distribution of proprietary source code",
                      "Patent retaliation clauses - Some licenses terminate patent rights if you initiate patent litigation",
                      "Attribution requirements - Failure to properly acknowledge FOSS components in your documentation",
                      "Incompatible license combinations - Using components with conflicting license terms in the same product",
                      "Unintentional distribution - SaaS deployments may inadvertently trigger distribution requirements",
                      "Derivative work uncertainty - Unclear boundaries between your code and FOSS components",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <WarningIcon sx={{ color: "#e65100", mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #81c784",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#2e7d32" }}
                  >
                    Best Practices for FOSS Management
                  </Typography>
                  <List>
                    {[
                      "Implement a formal open source policy and approval process",
                      "Maintain a complete inventory of all FOSS components and their licenses",
                      "Use automated scanning tools to detect open source in your codebase",
                      "Create proper attribution notices and license compliance documentation",
                      "Segregate copyleft code from proprietary code through appropriate architectural boundaries",
                      "Train developers on FOSS license implications and compliance requirements",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <CheckCircleOutlineIcon
                          sx={{ color: "#2e7d32", mr: 1 }}
                        />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Common FOSS Compliance Pitfalls
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Transitive Dependencies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Modern software often includes dependencies that
                        themselves have dependencies. A seemingly harmless
                        package can pull in components with problematic
                        licenses. Complete dependency tree analysis is essential
                        to identify these hidden compliance issues.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Dynamic Linking Considerations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Some licenses like the LGPL treat static and dynamic
                        linking differently. Understanding how your code
                        interfaces with FOSS components can determine whether
                        license obligations are triggered. Static linking
                        generally creates tighter integration that may trigger
                        more stringent obligations.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        License Notices Removal
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Developers sometimes inadvertently remove license
                        notices when incorporating open source code. This
                        violates almost all FOSS licenses and can create
                        significant compliance issues. Proper processes must
                        preserve all original notices.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        M&A Due Diligence
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        FOSS issues are often overlooked in acquisitions,
                        leading to unexpected post-merger compliance problems.
                        Thorough FOSS audits during due diligence can identify
                        potential issues before they impact transaction value or
                        integration plans.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="FOSS License Review Services"
              content="Our law firm provides comprehensive FOSS compliance services including license audits, policy development, architectural recommendations, and remediation strategies. We can help ensure your commercial software effectively leverages open source while minimizing legal exposure. Schedule a consultation to discuss how we can help protect your software investments."
              type="success"
              icon={<SearchIcon />}
              link="#contact-section"
              linkText="Schedule FOSS Review"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 7. Free and Open Source Software Licenses */}
      <Box id="foss-licenses" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <LibraryBooksIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Free and Open Source Software Licenses
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Free and Open Source Software (FOSS) licenses govern the terms
              under which software can be used, modified, and distributed.
              Understanding the various types of licenses is crucial when
              incorporating open source components into your projects or
              deciding to open source your own software.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Types of FOSS Licenses
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#f9fbff",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                      color="#0d47a1"
                    >
                      Permissive Licenses
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Provide basic attribution requirements with minimal
                      restrictions on usage, modification, and distribution.
                    </Typography>
                    <Typography variant="body2">Examples:</Typography>
                    <List dense>
                      {[
                        "MIT License",
                        "Apache License 2.0",
                        "BSD License Family",
                        "ISC License",
                      ].map((item, index) => (
                        <ListItem key={index} dense sx={{ py: 0.25 }}>
                          <ChevronRightIcon
                            sx={{ fontSize: "0.8rem", mr: 1, color: "#0d47a1" }}
                          />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Commercial applications,
                      proprietary software, products requiring maximum
                      flexibility
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#fffde7",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                      color="#f57f17"
                    >
                      Weak Copyleft Licenses
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Require changes to the licensed code to be shared, but
                      don't extend to the larger work.
                    </Typography>
                    <Typography variant="body2">Examples:</Typography>
                    <List dense>
                      {[
                        "GNU Lesser GPL (LGPL)",
                        "Mozilla Public License (MPL)",
                        "Eclipse Public License",
                        "Common Development and Distribution License",
                      ].map((item, index) => (
                        <ListItem key={index} dense sx={{ py: 0.25 }}>
                          <ChevronRightIcon
                            sx={{ fontSize: "0.8rem", mr: 1, color: "#f57f17" }}
                          />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Libraries and components used
                      in larger applications, projects needing balance between
                      sharing and integration
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#fbe9e7",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                      color="#bf360c"
                    >
                      Strong Copyleft Licenses
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Require all derivative works to be distributed under the
                      same license terms.
                    </Typography>
                    <Typography variant="body2">Examples:</Typography>
                    <List dense>
                      {[
                        "GNU General Public License (GPL)",
                        "GNU Affero GPL (AGPL)",
                        "Open Software License (OSL)",
                        "Reciprocal Public License",
                      ].map((item, index) => (
                        <ListItem key={index} dense sx={{ py: 0.25 }}>
                          <ChevronRightIcon
                            sx={{ fontSize: "0.8rem", mr: 1, color: "#bf360c" }}
                          />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Community-focused projects,
                      software intended to remain open source, projects where
                      preventing proprietary forks is important
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Considerations for Open Sourcing Your AI Application
              </Typography>
              <Typography variant="body2" paragraph>
                Open sourcing your AI technology can provide numerous benefits
                including community contributions, increased adoption, and
                reputational advantages. However, it requires careful strategic
                planning:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        License Selection Strategy
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Different components may benefit from different
                        licenses. Core algorithms might use permissive licenses
                        to encourage adoption, while specific implementations
                        could use copyleft licenses to ensure improvements
                        remain open source.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Dual Licensing Models
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Entails offering your software under both open source
                        and commercial licenses. This allows free use by the
                        community while enabling commercial licensing revenue
                        from enterprises needing additional rights or support.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: "flex" }}>
                <Button
                  startIcon={<LaunchIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: "#4ABDAC",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#2D93AD",
                      color: "#fff",
                    },
                  }}
                  component={RouterLink}
                  to="/ai-models?sourceType=open_source"
                >
                  Explore Open Source AI Models
                </Button>
              </Box>
            </Box>

            <InfoBox
              title="Open Source Licensing Consultation"
              content="If you're considering open sourcing your AI application or technology, our team can help you develop a licensing strategy that aligns with your business objectives. We'll help you select appropriate licenses, create contribution guidelines, and develop governance structures that protect your interests while fostering community growth."
              type="primary"
              link="#contact-section"
              linkText="Schedule Consultation"
            />
            {/* Divider with Text */}
            <Box
              id="foss-licenses-table"
              sx={{
                display: "flex",
                alignItems: "center",
                my: 4,
              }}
            >
              <Divider sx={{ flex: 1 }} />
              <Box
                sx={{
                  px: 2,
                  color: tertColor,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Example FOSS Licenses
              </Box>
              <Divider sx={{ flex: 1 }} />
            </Box>

            <KthSoftLicTable
              compareBy={compareBy}
              handleCompareChange={handleCompareChange}
              ComparisonOptions={ComparisonOptions}
              licenses={licenses}
            />
          </CardContent>
        </Card>
      </Box>

      {/* ======== CONTACT / CTA SECTION ======== */}
      <Box
        component="section"
        id="contact-section"
        sx={{
          mt: 0,
          p: 4,
          borderRadius: 2,
          backgroundColor: "#f3f6f9",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${tertColor}33`,
        }}
      >
        {/* Title spanning both columns */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight="bold" color={tertColor}>
            Secure Your Software & Technology Assets
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            For Immediate Assistance, Call Us At:{" "}
            <Chip
              label="+1-602-837-0631"
              color="primary"
              sx={{
                backgroundColor: tertColor,
                fontWeight: "bold",
                fontSize: "1rem",
                height: "32px",
                "& .MuiChip-label": {
                  color: "white",
                },
              }}
            />
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT SIDE: Overview */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: tertColor, mb: 2 }}
              >
                Our Software Licensing & Copyright Services:
              </Typography>

              <List>
                {[
                  "Comprehensive software license audits and compliance reviews",
                  "FOSS license risk assessment and remediation strategies",
                  "Custom EULA and software license agreement drafting",
                  "Strategic IP protection planning combining patents, copyright, and trade secrets",
                  "AI licensing and training data agreements",
                  "Software copyright registration and enforcement",
                  "Open source licensing strategy for your proprietary code",
                ].map((text, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <CheckCircleOutlineIcon
                      sx={{ color: tertColor, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {text}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={tertColor}
                >
                  Our Approach
                </Typography>
                <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                  We understand that technology companies need practical,
                  business-focused legal solutions. We combine deep technical
                  knowledge with legal expertise to provide guidance that
                  protects your intellectual property while supporting your
                  business objectives and innovation goals.
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT SIDE: Contact Form */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: tertColor, color: "white" }}>
                <Typography variant="h5" fontWeight="medium" align="center">
                  Schedule Your Software Licensing Consultation
                </Typography>
              </Box>
              <CardContent>
                <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
                  Please complete the form below, and our team will reach out to
                  schedule a consultation with one of our technology licensing
                  attorneys.
                </Typography>

                <EmbededContactForm
                  dropDownOptions={[
                    "Software License Review",
                    "FOSS Compliance Audit",
                    "EULA Development",
                    "Copyright Registration",
                    "Open Source Strategy",
                    "AI Licensing Issues",
                    "Software Patent Questions",
                    "Other Software/Technology Matter",
                  ]}
                  submitButtonLabel="Schedule My Consultation"
                  disclaimerHTML="Please note that submitting this form does not create an attorney-client relationship. Any information shared will be kept confidential."
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* ======== FINAL DISCLAIMER ======== */}
      <Box
        mt={6}
        pt={2}
        borderTop="1px solid #ccc"
        sx={{
          backgroundColor: "#f9f9f9",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          Disclaimer: Please do not share confidential details until we confirm
          our representation. This website provides general information and is
          not intended as legal advice for your specific situation.
        </Typography>
      </Box>
    </Container>
  );
};

export default KthSoftLic;
